import {
  ModuleCategory,
  OptionalString,
  PublishesTo,
  SchemaTypeHelper,
  StringEnum,
  SubscribesTo,
  createComponentAdmin,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'Countdown';
export const name = 'Countdown Clock';
export const description = reactName;
const category: ModuleCategory = 'preset';

export interface UnitTmplGetterParams {
  removeDivider?: boolean;
}

export type UnitOfTime = 'days' | 'hours' | 'minutes' | 'seconds';

export type UnitLabels = Partial<Record<UnitOfTime, string>>;

export const DefaultUnitLabels: Record<UnitOfTime, string> = {
  days: 'DAYS',
  minutes: 'MINUTES',
  hours: 'HOURS',
  seconds: 'SECONDS',
};

export const Dividers = {
  FORWARDSLASH: '/',
  HYPHEN: '-',
  PIPE: '|',
  NONE: 'NONE',
  COLON: ':',
} as const;

export const schema = Type.Object({
  titleText: OptionalString({title: 'Title Text', default: 'DOORS OPEN IN'}),
  endTime: Type.String({
    title: 'Target Time',
    default: '2025-02-25T20:25:20.252Z',
  }),
  typography: Type.Optional(
    Type.Object(
      {
        fontFamily: OptionalString('Font Family'),
        primaryTextColor: OptionalString({
          title: 'Primary Text Color',
          examples: ['#000'],
        }),
        titleFontSize: OptionalString({title: 'Title Font Size'}),
        clockFontSize: OptionalString({
          title: 'Clock Font Size',
        }),
        unitFontSize: OptionalString({title: 'Unit Label Font Size'}),
      },
      {
        default: {
          primaryTextColor: '#000000',
          clockFontSize: '70px',
        },
      }
    )
  ),
  isCountdownColumn: Type.Optional(
    Type.Boolean({title: 'Display Countdown Vertically'})
  ),
  isUnitUnderNum: Type.Optional(
    Type.Boolean({title: 'Display unit under number'})
  ),
  divider: StringEnum(
    [
      Dividers.COLON,
      Dividers.FORWARDSLASH,
      Dividers.HYPHEN,
      Dividers.PIPE,
      Dividers.NONE,
    ],
    {default: Dividers.COLON}
  ),
  unitLabels: Type.Optional(
    Type.Object(
      {
        days: OptionalString({title: 'Days', default: DefaultUnitLabels.days}),
        hours: OptionalString({
          title: 'Hours',
          default: DefaultUnitLabels.hours,
        }),
        minutes: OptionalString({
          title: 'Minutes',
          default: DefaultUnitLabels.minutes,
        }),
        seconds: OptionalString({
          title: 'Seconds',
          default: DefaultUnitLabels.seconds,
        }),
      },
      {title: 'Time Unit Labels'}
    )
  ),
});

export const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              [
                'General',
                [
                  'titleText',
                  'endTime',
                  'isCountdownColumn',
                  'isUnitUnderNum',
                  'divider',
                  'unitLabels',
                ],
              ],
              ['Typography', ['typography']],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [['Custom Styles', ['styleAttr']]],
          },
        ],
      ],
      [
        'Animations',
        [
          {
            'ui:template': 'accordion',
            sections: [['Animations', ['animationStates']]],
          },
        ],
      ],
    ],
  },
  typography: {
    primaryTextColor: {
      'ui:widget': 'color',
    },
  },
  endTime: {
    'ui:widget': 'date-time',
  },
};

export const defaultFieldData: Static<typeof schema> = {
  divider: schema.properties.divider.default,
  endTime: schema.properties.endTime.default,
  titleText: schema.properties.titleText.default,
  typography: schema.properties.typography.default,
  unitLabels: {
    days: schema.properties.unitLabels.properties.days.default,
    hours: schema.properties.unitLabels.properties.hours.default,
    minutes: schema.properties.unitLabels.properties.minutes.default,
    seconds: schema.properties.unitLabels.properties.seconds.default,
  },
};

const targetTime = Type.String({
  description: 'The timestamp that this countdown is heading towards',
  format: 'date-time',
});

const instructions = Type.Union([
  // This may look strange, but it's for backwards compatibility, for the countdown timer's
  // state machine, and we don't want the Flow editor to provide an emitter for this event anyway.
  PublishesTo({
    topic: `${reactName}:complete`,
    description: 'DEPRECATED Countdown has completed (timer has run down)',
    options: {
      '$lcd-flow-ignore': true,
    },
  }),
  PublishesTo({
    topic: `${reactName}:on-complete`,
    description: 'Countdown has completed (timer has run down)',
    meta: {
      currentTime: Type.String({
        title: 'Current Time',
        description: 'The timestamp when the countdown completed',
      }),
    },
  }),
  PublishesTo({
    topic: `${reactName}:ended`,
    description: 'Countdown has been explicitly ended',
    meta: {
      currentTime: Type.String({
        title: 'Current Time',
        description: 'The timestamp when the countdown completed',
        format: 'date-time',
      }),
    },
  }),
  PublishesTo({
    topic: `${reactName}:started`,
    description: 'Countdown has started',
    meta: {targetTime},
  }),
  SubscribesTo({
    topic: `${reactName}:changeTimezone`,
    description: 'Updates the timezone of the countdown',
    meta: {
      timezoneOffset: Type.Number({
        title: 'Timezone Offset',
        description:
          'The offset of the display timezone from UTC in minutes (positive indicates local is behind UTC; negative indicates ahead of UTC).',
      }),
    },
  }),
  SubscribesTo({
    topic: `${reactName}:ready`,
    description: 'Start the countdown even if the start time is later',
  }),
  PublishesTo({
    topic: `${reactName}:on-ready`,
    description:
      'The countdown has begun in the browser. NOTE: this will not fire if the component mounts after the countdown ends.',
  }),
  SubscribesTo({
    topic: `${reactName}:setTargetTime`,
    description: 'Updates the `targetTime` of the countdown',
    meta: {targetTime},
  }),
  SubscribesTo({
    topic: `${reactName}:tick`,
    description: 'Countdown tick has passed',
    meta: {
      currentTime: Type.String({
        title: 'Current Time',
        description: 'The timestamp when the tick occurred.',
        format: 'date-time',
      }),
    },
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: 'bd8ed176-e441-45bf-b0f8-ef3d28fdc4cc',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema,
  instructions,
  category,
})
  .withAnimationStates()
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
